import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import weekday from 'dayjs/plugin/weekday';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
    Text,
    Checkbox,
    StyledCard,
    StyledCardContent,
    ContainedButton,
    PlusCircleIcon,
    PopupMenu,
    MonthSelector,
} from '../../index';

import { StyledDay, StyledDayTextContainer, StyledDayEventContainer } from './style';

import { DAYS } from '../../../constant';

dayjs.extend(isSameOrAfter);
dayjs.extend(weekday);

const CalendarCard = ({
    calendarData,
    selectedDay,
    setSelectedDay,
    selectedDays,
    setSelectedDays,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    xlUp,
    isManage,
    selectedStaff,
    disabled,
}) => {
    const history = useHistory();

    const actionButtonRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    // hide picker if click somewhere else
    const handleClickOutside = (event) => {
        if (actionButtonRef.current && !actionButtonRef.current.contains(event.target)) {
            setAnchorEl(false);
        }
    };
    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    }, []);

    const [selectedColumns, setSelectedColumns] = useState([]);

    useEffect(() => {
        if (_.isEmpty(selectedDays)) {
            setSelectedColumns([]);
        }
    }, [selectedDays]);

    const handleSelectOneDay = (value) => {
        if (isManage && value.date.isBefore(dayjs(), 'date')) return;

        setSelectedDay(value);
        setSelectedDays([]);
    };

    const handleSelectDay = (value, indexOfDay) => {
        let selectedDaysOfMonth = [];
        calendarData.forEach((week) => selectedDaysOfMonth.push(week[indexOfDay]));

        // filter out other months
        selectedDaysOfMonth = selectedDaysOfMonth.filter(
            (day) => day.date.month() === selectedMonth
        );

        const newSelectedDays = [...selectedDays];
        if (selectedDays.includes(value)) {
            newSelectedDays.splice(newSelectedDays.indexOf(value), 1);
            setSelectedDays(newSelectedDays);

            if (_.isEmpty(newSelectedDays)) {
                setSelectedDay({});
            }

            setSelectedColumns([...selectedColumns].filter((day) => day !== indexOfDay));
        } else {
            newSelectedDays.push(value);
            setSelectedDays(newSelectedDays);

            if (selectedDaysOfMonth.every((day) => newSelectedDays.includes(day))) {
                setSelectedColumns([...selectedColumns, indexOfDay]);
            }
        }
    };

    const handleSelectColumn = (checked, indexOfDay) => {
        let selectedDaysOfMonth = [];
        calendarData.forEach((week) => selectedDaysOfMonth.push(week[indexOfDay]));

        // filter out other months and past date
        selectedDaysOfMonth = selectedDaysOfMonth.filter(
            (day) => day.date.isSameOrAfter(dayjs(), 'day') && day.date.month() === selectedMonth
        );

        if (checked) {
            setSelectedDays(
                [...selectedDays, ...selectedDaysOfMonth].sort((a, b) =>
                    dayjs(a).isAfter(dayjs(b)) ? 1 : -1
                )
            );

            setSelectedColumns([...selectedColumns, indexOfDay]);
        } else {
            let newSelectedDays = [...selectedDays];
            newSelectedDays = newSelectedDays.filter((day) => !selectedDaysOfMonth.includes(day));
            setSelectedDays(newSelectedDays.sort((a, b) => (dayjs(a).isAfter(dayjs(b)) ? 1 : -1)));

            const indexOfColumn = selectedColumns.indexOf(indexOfDay);
            const newSelectedColumns = [...selectedColumns];
            newSelectedColumns.splice(indexOfColumn, 1);
            setSelectedColumns(newSelectedColumns);
        }
    };

    const onCreateGroupSession = () => {
        history.push({
            pathname: `/provider/calendar/group/add`,
            search: `?date=${selectedDay.date}&staff=${selectedStaff}`,
        });
    };
    const onCreateOneOnOneSession = () => {
        history.push({
            pathname: `/provider/calendar/one/add`,
            search: `?date=${selectedDay.date}&staff=${selectedStaff}`,
        });
    };
    const onCreateOpenSession = () => {
        history.push({
            pathname: `/provider/calendar/open/add`,
            search: `?date=${selectedDay.date}&staff=${selectedStaff}`,
        });
    };

    return (
        <StyledCard padding="25px 20px 20px">
            <StyledCardContent>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <MonthSelector
                        selectedMonth={selectedMonth}
                        selectedYear={selectedYear}
                        setSelectedMonth={setSelectedMonth}
                        setSelectedYear={setSelectedYear}
                    />
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            columnGap: '20px',
                        }}
                    >
                        <div ref={actionButtonRef} style={{ position: 'relative', width: '150px' }}>
                            <PopupMenu
                                open={open}
                                right="0"
                                bottom="-130px"
                                onClose={handleClose}
                                options={[
                                    {
                                        text: 'Group Session',
                                        onClick: onCreateGroupSession,
                                    },
                                    {
                                        text: '1 on 1 Session',
                                        onClick: onCreateOneOnOneSession,
                                    },
                                    {
                                        text: 'Open Session',
                                        onClick: onCreateOpenSession,
                                    },
                                ]}
                            />
                            <ContainedButton
                                $backgroundColor="green7"
                                $height="36px"
                                style={{ padding: '0 10px 0 0' }}
                                onClick={handleClick}
                                disabled={
                                    dayjs(selectedDay.date).isBefore(dayjs(), 'day') ||
                                    disabled ||
                                    selectedStaff === ''
                                }
                            >
                                <PlusCircleIcon fill="white" width="20" height="20" />
                                <Text $fontSize="14px" $color="white" style={{ marginLeft: '5px' }}>
                                    Add session
                                </Text>
                            </ContainedButton>
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: '20px' }}>
                    <div
                        style={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7,1fr)',
                        }}
                    >
                        {DAYS.map((day, index) => (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    columnGap: '5px',
                                }}
                                key={day}
                            >
                                {isManage && (
                                    <Checkbox
                                        checked={selectedColumns.includes(index)}
                                        onChange={(event) =>
                                            handleSelectColumn(event.target.checked, index)
                                        }
                                    />
                                )}
                                <Text
                                    $fontSize="13px"
                                    $color="gray3"
                                    style={{ textTransform: 'uppercase' }}
                                >
                                    {day}
                                </Text>
                            </div>
                        ))}
                    </div>
                    <div
                        style={{
                            marginTop: '5px',
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: '10px',
                        }}
                    >
                        {calendarData.length > 0 &&
                            calendarData.map((week, weekIndex) => (
                                <div
                                    key={`week-${weekIndex}`}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7,1fr)',
                                        columnGap: '10px',
                                        alignItems: 'stretch',
                                        minHeight: '85px',
                                    }}
                                >
                                    {week.map((day, index) => (
                                        <Day
                                            xlUp={xlUp}
                                            key={`day-${day.date.format('DD/MM/YYYY')}`}
                                            date={day.date}
                                            group={day.group}
                                            oneOnOne={day.oneOnOne}
                                            open={day.open}
                                            isCurrentMonth={day.date.month() === selectedMonth}
                                            isToday={day.date.isSame(dayjs(), 'date')}
                                            isSelected={
                                                (!isManage &&
                                                    day.date.isSame(selectedDay.date, 'date')) ||
                                                (isManage && !_.isEmpty(selectedDays)
                                                    ? selectedDays.includes(day)
                                                    : day.date.isSame(selectedDay.date, 'date'))
                                            }
                                            checked={isManage && selectedDays.includes(day)}
                                            onSelect={() => handleSelectOneDay(day)}
                                            onCheckBox={() => handleSelectDay(day, index)}
                                            isManage={isManage}
                                            isOnline={Boolean(day.onlineStart)}
                                        />
                                    ))}
                                </div>
                            ))}
                    </div>
                </div>
            </StyledCardContent>
        </StyledCard>
    );
};

CalendarCard.propTypes = {
    calendarData: PropTypes.array,
    selectedDay: PropTypes.object,
    setSelectedDay: PropTypes.func,
    selectedDays: PropTypes.array,
    setSelectedDays: PropTypes.func,
    selectedMonth: PropTypes.number,
    setSelectedMonth: PropTypes.func,
    selectedYear: PropTypes.number,
    setSelectedYear: PropTypes.func,
    xlUp: PropTypes.bool,
    isManage: PropTypes.bool,
    selectedStaff: PropTypes.string,
    disabled: PropTypes.bool,
};

export default CalendarCard;

const Day = ({
    isManage,
    isCurrentMonth,
    date,
    onSelect,
    onCheckBox,
    isToday,
    group,
    oneOnOne,
    open,
    isOnline,
    isSelected,
    checked,
    xlUp,
}) => {
    const [showCheckbox, setShowCheckbox] = useState(false);

    useEffect(() => {
        if (isManage && isCurrentMonth && date.isSameOrAfter(dayjs(), 'day')) {
            return setShowCheckbox(true);
        }

        setShowCheckbox(false);
    }, [isManage, isCurrentMonth, date]);

    return (
        <StyledDay
            $big={xlUp}
            $isSelected={isSelected}
            onClick={onSelect}
            isCurrentMonth={isCurrentMonth}
            isOnline={isOnline}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: showCheckbox ? 'space-between' : 'flex-end',
                }}
            >
                {showCheckbox && (
                    <Checkbox
                        fill="green4"
                        checked={checked}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCheckBox();
                        }}
                    />
                )}
                <StyledDayTextContainer isToday={isToday}>
                    <Text
                        $fontSize="12px"
                        $color={isToday ? 'white' : isCurrentMonth ? 'black' : 'gray2'}
                    >
                        {date.format('D')}
                    </Text>
                </StyledDayTextContainer>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '3px',
                }}
            >
                {group > 0 && isCurrentMonth && (
                    <DayEvent
                        $big={xlUp}
                        $backgroundColor="purple"
                        $color="white"
                        eventText="GROUP"
                        eventNumber={group}
                    />
                )}
                {oneOnOne > 0 && isCurrentMonth && (
                    <DayEvent
                        $big={xlUp}
                        $backgroundColor="pink"
                        $color="white"
                        eventText="1 ON 1"
                        eventNumber={oneOnOne}
                    />
                )}
                {open > 0 && isCurrentMonth && (
                    <DayEvent
                        $big={xlUp}
                        $backgroundColor="green"
                        $color="white"
                        eventText="OPEN"
                        eventNumber={open}
                    />
                )}
            </div>
        </StyledDay>
    );
};

Day.propTypes = {
    isManage: PropTypes.bool,
    isCurrentMonth: PropTypes.bool,
    isOnline: PropTypes.bool,
    date: PropTypes.object,
    xlUp: PropTypes.bool,
    onSelect: PropTypes.func,
    isToday: PropTypes.bool,
    group: PropTypes.number,
    one: PropTypes.number,
    checked: PropTypes.bool,
    isSelected: PropTypes.bool,
    oneOnOne: PropTypes.number,
    open: PropTypes.number,
    onCheckBox: PropTypes.func,
};

const DayEvent = ({ $backgroundColor, $color, eventText, eventNumber, $big }) => {
    return (
        <StyledDayEventContainer $backgroundColor={$backgroundColor} $big={$big}>
            {$big && (
                <Text $color={$color} $fontSize="10px">
                    {eventText}
                </Text>
            )}
            <Text $color={$color} $fontSize="10px">
                {eventNumber}
            </Text>
        </StyledDayEventContainer>
    );
};

DayEvent.propTypes = {
    $backgroundColor: PropTypes.string,
    $color: PropTypes.string,
    $big: PropTypes.bool,
    eventText: PropTypes.string,
    eventNumber: PropTypes.number,
};
